import type { SyberryTodayTaskId, SyberryTodayWorkItem } from 'domain/people/syberryToday/types';
import type { FocusTimeSlot } from 'domain/people/availability/focusTime/types';
import type { Theme } from '@mui/material/styles';
import FocusTimeDayTimeline from 'domain/people/availability/focusTime/shared/FocusTimeDayTimeline/FocusTimeDayTimeline';
import { MIN_DAY_HEIGHT } from 'domain/people/availability/focusTime/shared/FocusTimeDayTimeline/layout';
import SyberryTodayCountBadge, { SyberryTodayCountBadgeColor } from '../SyberryTodayCountBadge/SyberryTodayCountBadge';
import SyberryTodayOrganizeTaskRow from './SyberryTodayOrganizeTaskRow';
import { Box, Typography } from '@mui/material';

type SyberryTodayOrganizeBoardProps = {
  tasks: SyberryTodayWorkItem[];
  focusTimeCurrentMinutes: number | null;
  focusTimeSlots: FocusTimeSlot[];
  expandedTaskId: Nullable<SyberryTodayTaskId>;
  onChangeFocusTimeSlots: (slots: FocusTimeSlot[]) => void;
  onToggleExpansion: (taskId: SyberryTodayTaskId) => void;
};

const ROOT_SX = {
  display: 'flex',
  minHeight: 0,
  minWidth: 0,
  alignSelf: 'stretch',
  flexDirection: 'column',
  marginLeft: 2.5,
  px: { xs: 2, lg: 3 },
  py: { xs: 2, lg: 3 },
  backgroundColor: 'background.paper',
  '@media (max-width: 900px)': {
    marginLeft: 0,
  },
} as const;

const HEADER_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minHeight: 40,
} as const;

const SECTION_TITLE_SX = {
  lineHeight: 1.15,
} as const;

const CONTENT_SX = (theme: Theme) => ({
  display: 'grid',
  flex: '1 1 auto',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  minHeight: 0,
  columnGap: 4,
  rowGap: 3,
  alignItems: 'stretch',
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'minmax(0, 1fr)',
  },
});

const SECTION_SX = {
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  rowGap: 1.25,
} as const;

const TASK_LIST_SX = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1.25,
} as const;

const FOCUS_PANEL_SX = {
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  rowGap: 1.25,
} as const;

const FOCUS_TIMELINE_SX = {
  flex: '1 1 auto',
  minHeight: MIN_DAY_HEIGHT,
  minWidth: 0,
} as const;

const SyberryTodayOrganizeBoard = ({
  tasks,
  focusTimeCurrentMinutes,
  focusTimeSlots,
  expandedTaskId,
  onChangeFocusTimeSlots,
  onToggleExpansion,
}: SyberryTodayOrganizeBoardProps) => (
  <Box component="main" sx={ROOT_SX}>
    <Box sx={CONTENT_SX}>
      <Box component="section" aria-label="Do today tasks" sx={SECTION_SX}>
        <Box sx={HEADER_SX}>
          <Typography variant="h1" color="brand" sx={SECTION_TITLE_SX}>
            Do Today Tasks
          </Typography>
          <SyberryTodayCountBadge
            value={tasks.length}
            aria-label={`${tasks.length} selected tasks`}
            color={SyberryTodayCountBadgeColor.PRIMARY}
          />
        </Box>

        {tasks.length ? (
          <Box sx={TASK_LIST_SX}>
            {tasks.map(task => (
              <SyberryTodayOrganizeTaskRow
                key={task.id}
                task={task}
                isExpanded={expandedTaskId === task.id}
                onToggleExpansion={onToggleExpansion}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
            No selected tasks
          </Typography>
        )}
      </Box>

      <Box component="section" aria-label="Focus time schedule" sx={FOCUS_PANEL_SX}>
        <Typography variant="h2" color="brand" sx={SECTION_TITLE_SX}>
          My Day
        </Typography>

        <Box sx={FOCUS_TIMELINE_SX}>
          <FocusTimeDayTimeline
            slots={focusTimeSlots}
            currentMinutes={focusTimeCurrentMinutes}
            onChange={onChangeFocusTimeSlots}
          />
        </Box>
      </Box>
    </Box>
  </Box>
);

export default SyberryTodayOrganizeBoard;
